import type { SelectProps } from '@risk-smart/themed-cloudscape-components/select';
import { type FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import HideableSelect from '@/components/form/select/HideableSelect';
import { useGroupAndUserOptions } from '@/components/user-search-preferences/useGroupAndUserOptions';

import type { MyItemsTarget } from '../useDashboardStore';
import { defaultMyItemsFilter, useDashboardStore } from '../useDashboardStore';
import styles from './style.module.scss';

// Auto-applied when the user picks a target. Direct flags on so the picker
// surfaces everything the target is directly related to; user can untick or
// add inherited flags to narrow/widen afterwards.
const targetAutoFilters = {
  owner: true,
  contributor: true,
  groupOwner: true,
  groupContributor: true,
  inheritedOwner: false,
  inheritedContributor: false,
  inheritedGroupOwner: false,
  inheritedGroupContributor: false,
};

const findOption = (
  opts: readonly (SelectProps.Option | SelectProps.OptionGroup)[] | undefined,
  value: string
): SelectProps.Option | null => {
  if (!opts) {
    return null;
  }
  for (const opt of opts) {
    if ('options' in opt && opt.options) {
      const found = findOption(opt.options, value);
      if (found) {
        return found;
      }
    } else if ('value' in opt && opt.value === value) {
      return opt;
    }
  }

  return null;
};

const DashboardTargetPicker: FC = () => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'dashboard.myItemsDashboard.targetPicker',
  });
  const { myItemsTarget, setMyItemsTarget, setMyItemsFilters } =
    useDashboardStore();
  const { optionItems, statusType } = useGroupAndUserOptions({
    includeGroups: true,
    addEmptyOption: true,
    emptyOptionLabel: t('clearLabel'),
  });

  const selectedOption = useMemo<SelectProps.Option | null>(
    () =>
      myItemsTarget
        ? findOption(
            optionItems as readonly (
              | SelectProps.Option
              | SelectProps.OptionGroup
            )[],
            myItemsTarget.id
          )
        : null,
    [optionItems, myItemsTarget]
  );

  // If the previously selected target no longer appears in the option list
  // (user/group deleted or otherwise filtered out), clear it so the filter
  // stops applying. Wait until options have loaded (statusType !== 'loading')
  // to avoid clearing while the list is still streaming in.
  useEffect(() => {
    if (myItemsTarget && !selectedOption && statusType !== 'loading') {
      setMyItemsTarget(null);
      setMyItemsFilters(defaultMyItemsFilter);
    }
  }, [
    myItemsTarget,
    selectedOption,
    statusType,
    setMyItemsTarget,
    setMyItemsFilters,
  ]);

  return (
    <div className={styles.targetPicker}>
      <HideableSelect
        data-testid={'my-items-target-picker'}
        selectedOption={selectedOption}
        placeholder={t('placeholder')}
        statusType={statusType}
        options={optionItems as SelectProps.Options}
        filteringType={'auto'}
        empty={t('empty')}
        onChange={({ detail }) => {
          const opt = detail.selectedOption;
          // Empty option (value '') or unset → restore self-mode defaults.
          if (!opt?.value) {
            setMyItemsTarget(null);
            setMyItemsFilters(defaultMyItemsFilter);

            return;
          }
          const isGroup = (opt as { type?: string }).type === 'userGroup';
          const next: MyItemsTarget = isGroup
            ? { type: 'group', id: opt.value }
            : { type: 'user', id: opt.value };
          setMyItemsTarget(next);
          setMyItemsFilters(targetAutoFilters);
        }}
      />
    </div>
  );
};

export default DashboardTargetPicker;
